import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { openRazorpayCheckout } from "../lib/razorpay";
import { PerUnit } from "./workspace/shared";
import { STATES } from "./workspace/Checkout";
import {
  ArrowRight, Boxes, Clock3, MapPin, IndianRupee, PlusCircle,
  Loader2, LogOut, RefreshCw, CheckCircle2, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * /printer/materials — Printer-side material RFQ console.
 *
 * Shows the printer's awarded jobs, lets them post a sealed sub-RFQ for
 * raw materials against any awarded job, and lists all RFQs they've created
 * with the bids received against each one.
 */
export default function PrinterMaterials() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState("");

  const bootstrap = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      if (r.data?.role !== "printer" && r.data?.role !== "admin") {
        nav("/login"); return;
      }
      setMe(r.data);
    } catch { nav("/login"); }
  }, [nav]);

  const refresh = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [rfqsR, jobsR] = await Promise.all([
        axios.get(`${API}/material-rfqs`),
        axios.get(`${API}/jobs?status=awarded`).catch(() => ({ data: [] })),
      ]);
      setRfqs(rfqsR.data?.rfqs || []);
      // keep only jobs awarded to me
      const myJobs = (Array.isArray(jobsR.data) ? jobsR.data : jobsR.data?.jobs || [])
        .filter(j => j?.awarded_supplier_id === me?.id);
      setJobs(myJobs);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load material RFQs.");
    } finally { setLoading(false); }
  }, [me]);

  useEffect(() => { bootstrap(); }, [bootstrap]);
  useEffect(() => { if (me) refresh(); }, [me, refresh]);

  const logout = async () => { try { await axios.post(`${API}/auth/logout`); } catch {} nav("/login"); };

  if (!me) return <div className="mat-boot"><Loader2 className="mat-spin"/> Loading…</div>;

  return (
    <div className="mat-shell" data-testid="printer-materials-page">
      <header className="mat-nav">
        <Link to="/" className="m-brand"><img src="/logo.png" alt="GoPrintPack" className="m-brand-logo"/></Link>
        <div className="mat-nav-mid">
          <span className="mat-chip" style={{background:"#EFF6FF",color:"#1E3A8A"}}>Printer · Materials</span>
          <h1 className="mat-nav-title">Request materials & finishing for platform or your own jobs</h1>
        </div>
        <div className="mat-nav-actions">
          <button className="m-cta" onClick={() => setShowNew(true)} data-testid="printer-materials-new">
            <PlusCircle size={14}/> New RFQ
          </button>
          <button className="m-ghost" onClick={refresh} data-testid="printer-materials-refresh">
            <RefreshCw size={14}/> Refresh
          </button>
          <Link to="/workspace" className="m-ghost">Workspace</Link>
          <button className="m-ghost" onClick={logout}><LogOut size={14}/> Sign out</button>
        </div>
      </header>

      {err && <div className="mat-err">{err}</div>}

      {loading
        ? <div className="mat-boot"><Loader2 className="mat-spin"/> Loading your RFQs…</div>
        : rfqs.length === 0
          ? (
            <div className="mat-empty" data-testid="printer-materials-empty">
              <Boxes size={28}/>
              <p>No sub-RFQs yet. Post one for an awarded platform job or for your own (off-platform) job.</p>
            </div>
          )
          : (
            <section className="mat-list" data-testid="printer-materials-list">
              {rfqs.map(r => <PrinterRfqCard key={r.id} rfq={r} me={me}/>)}
            </section>
          )
      }

      {showNew && (
        <NewRfqModal
          jobs={jobs}
          me={me}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); refresh(); }}
        />
      )}

      <footer className="mat-foot">
        Need help? <a href="mailto:info@goprintpack.com">info@goprintpack.com</a>
      </footer>
    </div>
  );
}

/* ------- Printer RFQ card (bid list visible) ------- */
function PrinterRfqCard({ rfq, me }) {
  const [bids, setBids] = useState(null);
  const [open, setOpen] = useState(false);
  const [awarding, setAwarding] = useState("");
  const [quote, setQuote] = useState(null);
  const [, bump] = useState(0);

  const loadBids = async () => {
    try {
      const r = await axios.get(`${API}/material-rfqs/${rfq.id}`);
      setBids(r.data?.bids || []);
    } catch {}
  };
  useEffect(() => { if (open && bids === null) loadBids(); }, [open]); // eslint-disable-line

  const startAward = async (bidId) => {
    try {
      const r = await axios.get(`${API}/material-rfqs/${rfq.id}/award-quote`, { params: { bid_id: bidId } });
      setQuote({ bidId, ...r.data });
    } catch (e) { alert(e.response?.data?.detail || "Could not price this award."); }
  };

  const payAndAward = async () => {
    const bidId = quote.bidId;
    setAwarding(bidId);
    try {
      const [cfg, order] = await Promise.all([
        axios.get(`${API}/razorpay/config`).then(r => r.data),
        axios.post(`${API}/material-rfqs/${rfq.id}/award-escrow/order`, { bid_id: bidId }).then(r => r.data),
      ]);
      const resp = await openRazorpayCheckout({
        keyId: cfg.key_id, order, name: "GoPrintPack · Sub-RFQ escrow",
        description: `${rfq.kind === "finishing" ? "Finishing" : "Material"} RFQ ${rfq.id}`,
        prefill: { name: me?.name || "", email: me?.email || "", contact: me?.phone || "" },
        notes: { kind: "sub_rfq_escrow", rfq_id: rfq.id, bid_id: bidId },
      });
      await axios.post(`${API}/razorpay/verify`, resp);
      const fresh = await axios.get(`${API}/material-rfqs/${rfq.id}`);
      Object.assign(rfq, fresh.data);
      setBids(fresh.data?.bids || []);
      setQuote(null);
    } catch (e) {
      alert(e?.code === "USER_CANCELLED" ? "Payment cancelled — escrow not funded." : (e?.description || e.response?.data?.detail || "Escrow payment failed."));
    } finally { setAwarding(""); }
  };

  const confirmReceipt = async () => {
    if (!window.confirm("Confirm you received this order in good condition? Escrow will be released to the supplier.")) return;
    try {
      const r = await axios.post(`${API}/material-rfqs/${rfq.id}/confirm-receipt`);
      Object.assign(rfq, r.data);
      bump(x => x + 1);
    } catch (e) { alert(e.response?.data?.detail || "Could not confirm receipt."); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this RFQ? Suppliers who bid will see it as closed.")) return;
    try {
      await axios.post(`${API}/material-rfqs/${rfq.id}/cancel`);
      rfq.status = "cancelled";
      setOpen(false);
    } catch (e) { alert(e.response?.data?.detail || "Could not cancel."); }
  };

  const statusChip = {
    live:      <span className="mat-open" data-testid={`printer-rfq-status-${rfq.id}`} style={{color:"#2563EB"}}>Live · {rfq.bid_count || 0} bids</span>,
    awarded:   <span className="mat-wonchip" data-testid={`printer-rfq-status-${rfq.id}`}><CheckCircle2 size={13}/> Awarded{rfq.escrow_status === "held" ? " · escrow held" : ""}</span>,
    dispatched: <span className="mat-wonchip" data-testid={`printer-rfq-status-${rfq.id}`}>Dispatched{rfq.tracking_number ? ` · ${rfq.carrier || ""} ${rfq.tracking_number}` : ""}</span>,
    received:  <span className="mat-wonchip" data-testid={`printer-rfq-status-${rfq.id}`}><CheckCircle2 size={13}/> Received · {rfq.escrow_status === "released" ? "paid to supplier" : "payout processing"}</span>,
    cancelled: <span className="mat-closed">Cancelled</span>,
  }[rfq.status] || null;

  return (
    <article className="mat-card" data-testid={`printer-rfq-${rfq.id}`}>
      <div className="mat-card-head" data-testid={`printer-rfq-head-${rfq.id}`} onClick={() => setOpen(x => !x)}>
        <div className="mat-card-icon" style={{background:"#EFF6FF",color:"#2563EB"}}><Boxes size={18}/></div>
        <div className="mat-card-meta">
          <div className="mat-card-cat" style={{color:"#2563EB"}}>{rfq.kind === "finishing" ? "Finishing" : "Material"} · {rfq.material_category.replace(/_/g, " ")}</div>
          <div className="mat-card-desc">{rfq.description}</div>
          <div className="mat-card-tags">
            {rfq.source === "own"
              ? <span data-testid={`printer-rfq-own-${rfq.id}`}>Own job: <b>{rfq.own_job_reference}</b></span>
              : <span>Job: <b>{rfq.job_id}</b></span>}
            <span><Clock3 size={11}/> {rfq.deadline_days}d</span>
            <span><MapPin size={11}/> {rfq.ship_to_pincode}</span>
            <span>Qty: <b>{rfq.quantity} {rfq.quantity_unit}</b></span>
          </div>
        </div>
        <div className="mat-card-cta">{statusChip}</div>
      </div>

      {open && (
        <div className="pr-rfq-expand" data-testid={`printer-rfq-expand-${rfq.id}`}>
          <div className="pr-rfq-bids">
            <h4>Bids received {bids ? `(${bids.length})` : "…"}</h4>
            {bids === null && <p className="pr-rfq-muted">Loading…</p>}
            {bids && bids.length === 0 && <p className="pr-rfq-muted">No bids yet. Suppliers will quote shortly.</p>}
            {bids && bids.map(b => (
              <div className="pr-bid" key={b.id} data-testid={`printer-rfq-bid-${b.id}`}>
                <div className="pr-bid-main">
                  <div className="pr-bid-price">₹{Number(b.price).toLocaleString("en-IN")}<PerUnit price={b.price} qty={rfq.quantity} unit={rfq.quantity_unit} testid={`printer-rfq-bid-per-unit-${b.id}`}/></div>
                  <div className="pr-bid-meta">
                    <span>{b.supplier_name}</span>
                    <span>· {b.turnaround_days}d TAT</span>
                    {b.note && <span>· <i>"{b.note}"</i></span>}
                  </div>
                </div>
                {rfq.status === "live" && (
                  <button className="m-cta" disabled={!!awarding}
                          onClick={() => startAward(b.id)}
                          data-testid={`printer-rfq-award-${b.id}`}>
                    Award & fund escrow <ArrowRight size={13}/>
                  </button>
                )}
                {rfq.status !== "live" && rfq.awarded_bid_id === b.id &&
                  <span className="mat-wonchip"><CheckCircle2 size={13}/> Awarded</span>}
              </div>
            ))}
          </div>
          {quote && rfq.status === "live" && (
            <div data-testid={`printer-rfq-quote-${rfq.id}`} style={{border:"1px solid #E2E8F0",background:"#F8FAFC",borderRadius:8,padding:"10px 14px",margin:"10px 0",fontSize:13}}>
              <div style={{display:"flex",justifyContent:"space-between"}}><span>Bid price</span><b data-testid="sub-quote-base">₹{quote.base_price_inr.toLocaleString("en-IN",{minimumFractionDigits:2})}</b></div>
              <div style={{display:"flex",justifyContent:"space-between"}}><span>GST {quote.gst_rate_pct}% ({quote.gst_split?.is_intra_state ? "CGST + SGST" : "IGST"})</span><b data-testid="sub-quote-gst">₹{quote.gst_inr.toLocaleString("en-IN",{minimumFractionDigits:2})}</b></div>
              <div style={{display:"flex",justifyContent:"space-between",borderTop:"1px solid #CBD5E1",marginTop:4,paddingTop:4,fontSize:15}}><span>Total into escrow</span><b data-testid="sub-quote-total">₹{quote.total_inr.toLocaleString("en-IN",{minimumFractionDigits:2})}</b></div>
              <p style={{fontSize:11,color:"#64748b",margin:"6px 0"}}>Funds stay in Razorpay escrow until you confirm receipt. Then they're released to the supplier.</p>
              <div style={{display:"flex",gap:8}}>
                <button className="m-ghost" onClick={() => setQuote(null)} data-testid="sub-quote-cancel">Back</button>
                <button className="m-cta" disabled={!!awarding} onClick={payAndAward} data-testid="sub-quote-pay">
                  {awarding ? "Opening Razorpay…" : `Pay ₹${quote.total_inr.toLocaleString("en-IN")} & award`}
                </button>
              </div>
            </div>
          )}
          {rfq.invoice_customer_id && (
            <div className="pr-rfq-footer">
              <a className="m-ghost" href={`${API}/invoices/${rfq.invoice_customer_id}/pdf`} target="_blank" rel="noreferrer"
                 data-testid={`printer-rfq-invoice-${rfq.id}`}>Download tax invoice {rfq.invoice_customer_number}</a>
            </div>
          )}
          {["awarded", "dispatched"].includes(rfq.status) && rfq.escrow_status === "held" && (
            <div className="pr-rfq-footer">
              <button className="m-cta" onClick={confirmReceipt} data-testid={`printer-rfq-confirm-${rfq.id}`}>
                Confirm receipt & release escrow
              </button>
            </div>
          )}
          {rfq.status === "live" && (
            <div className="pr-rfq-footer">
              <button className="m-ghost" onClick={cancel} data-testid={`printer-rfq-cancel-${rfq.id}`}>
                Cancel RFQ
              </button>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

/* ------- New RFQ modal ------- */
const OWN_MIN_VALUE = 5000;

function NewRfqModal({ jobs, me, onClose, onCreated }) {
  const [source, setSource] = useState(jobs.length ? "platform" : "own");
  const [addr, setAddr] = useState({
    line1: me?.address_line1 || me?.address || "", line2: "", city: me?.city || "", state: STATES.includes(me?.state) ? me.state : "",
    pincode: me?.pincode || "", contact_name: me?.name || "", contact_phone: me?.phone || "",
  });
  const setA = (e) => setAddr(a => ({ ...a, [e.target.name]: e.target.value }));
  const [form, setForm] = useState({
    job_id: jobs?.[0]?.id || "",
    own_job_reference: "",
    kind: "material",
    material_category: "paper",
    description: "",
    quantity: "", quantity_unit: "sheets",
    deadline_days: "4", ship_to_pincode: "",
    expected_price_hint: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (jobs.length && !form.job_id) { setForm(f => ({ ...f, job_id: jobs[0].id })); setSource("platform"); }
  }, [jobs, form.job_id]);

  const set = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const setKind = (kind) => setForm(f => ({ ...f, kind, material_category: kind === "finishing" ? "lamination" : "paper", quantity_unit: kind === "finishing" ? "pcs" : "sheets" }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const body = {
        ...form,
        quantity: Number(form.quantity),
        deadline_days: Number(form.deadline_days),
        expected_price_hint: form.expected_price_hint ? Number(form.expected_price_hint) : null,
      };
      if (source === "own") {
        await axios.post(`${API}/material-rfqs/own`, { ...body, source: "own", job_id: null, ship_to_pincode: addr.pincode, ship_to_address: addr });
      } else {
        const { own_job_reference, ...rest } = body;
        await axios.post(`${API}/jobs/${form.job_id}/material-rfqs`, { ...rest, source: "platform" });
      }
      onCreated();
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map(x => `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}`).join("; ") : (typeof d === "string" ? d : "Could not post RFQ."));
    } finally { setBusy(false); }
  };

  return (
    <div className="pr-modal-shade" onClick={onClose}>
      <div className="pr-modal" onClick={e => e.stopPropagation()} data-testid="printer-new-rfq-modal">
        <header className="pr-modal-head">
          <h3>Post a new {form.kind === "finishing" ? "finishing" : "material"} RFQ</h3>
          <button className="pr-modal-x" onClick={onClose}><X size={18}/></button>
        </header>
        <form onSubmit={submit} className="pr-modal-form">
          {err && <div className="mat-err" style={{margin:0}}>{err}</div>}
          <div style={{display:"flex",gap:8}} data-testid="printer-new-rfq-kind">
            {[["material", "Raw materials"], ["finishing", "Finishing work"]].map(([k, l]) => (
              <button type="button" key={k} data-testid={`printer-new-rfq-kind-${k}`} onClick={() => setKind(k)}
                className={form.kind === k ? "m-cta" : "m-ghost"}>{l}</button>
            ))}
          </div>
          <div style={{display:"flex",gap:8}} data-testid="printer-new-rfq-source">
            {[["platform", "GoPrintPack job"], ["own", "My own job (off-platform)"]].map(([k, l]) => (
              <button type="button" key={k} data-testid={`printer-new-rfq-source-${k}`} onClick={() => setSource(k)}
                className={source === k ? "m-cta" : "m-ghost"}>{l}</button>
            ))}
          </div>
          {source === "own" && !me?.is_verified && (
            <div className="mat-err" style={{margin:0}} data-testid="printer-new-rfq-kyc-warning">Complete KYC verification to post RFQs for your own jobs.</div>
          )}
          {source === "own" ? (
            <>
              <label className="pf-field">
                <span>Your job reference *</span>
                <input name="own_job_reference" value={form.own_job_reference} onChange={set} required maxLength={120}
                       placeholder="e.g. PO-2231 / Haldiram cartons" data-testid="printer-new-rfq-own-ref"/>
              </label>
              <fieldset className="pf-field" style={{border:"1px solid #E2E8F0",borderRadius:8,padding:10}} data-testid="printer-new-rfq-address">
                <span>Deliver to (your factory) *</span>
                <input name="line1" value={addr.line1} onChange={setA} required minLength={3} maxLength={200} placeholder="Address line 1" data-testid="printer-new-rfq-addr-line1"/>
                <input name="line2" value={addr.line2} onChange={setA} placeholder="Address line 2 (optional)" data-testid="printer-new-rfq-addr-line2"/>
                <div className="pr-modal-row pr-modal-row3">
                  <input name="city" value={addr.city} onChange={setA} required minLength={2} maxLength={80} placeholder="City" data-testid="printer-new-rfq-addr-city"/>
                  <select name="state" value={addr.state} onChange={setA} required data-testid="printer-new-rfq-addr-state">
                    <option value="">State</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <input name="pincode" value={addr.pincode} onChange={setA} required pattern="\d{6}" maxLength={6} placeholder="Pincode" data-testid="printer-new-rfq-addr-pincode"/>
                </div>
                <div className="pr-modal-row">
                  <input name="contact_name" value={addr.contact_name} onChange={setA} maxLength={80} placeholder="Receiver name" data-testid="printer-new-rfq-addr-contact"/>
                  <input name="contact_phone" value={addr.contact_phone} onChange={setA} maxLength={20} placeholder="Receiver phone" data-testid="printer-new-rfq-addr-phone"/>
                </div>
              </fieldset>
            </>
          ) : (
          <label className="pf-field">
            <span>Awarded job *</span>
            {jobs.length === 0
              ? <input value={form.job_id} name="job_id" onChange={set} required
                       placeholder="e.g. job-44a1fd48"
                       data-testid="printer-new-rfq-job-id"/>
              : <select name="job_id" value={form.job_id} onChange={set} required
                        data-testid="printer-new-rfq-job-select">
                  {jobs.map(j => <option key={j.id} value={j.id}>{j.id} — {j.title}</option>)}
                </select>}
          </label>
          )}
          <div className="pr-modal-row">
            <label className="pf-field">
              <span>{form.kind === "finishing" ? "Finishing process *" : "Material category *"}</span>
              <select name="material_category" value={form.material_category} onChange={set}
                      data-testid="printer-new-rfq-category">
                {(form.kind === "finishing" ? FINISHING_OPTS : MATERIAL_OPTS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            {source === "platform" && (
            <label className="pf-field">
              <span>Ship-to pincode *</span>
              <input name="ship_to_pincode" value={form.ship_to_pincode} onChange={set}
                     required maxLength={10} data-testid="printer-new-rfq-pincode"/>
            </label>
            )}
          </div>
          <label className="pf-field">
            <span>Specification / description *</span>
            <textarea name="description" rows={3} value={form.description} onChange={set}
                      minLength={10} maxLength={1500} required
                      className="pf-textarea"
                      placeholder="Kraft 300gsm, 28×40 sheets, FSC-certified preferred"
                      data-testid="printer-new-rfq-desc"/>
          </label>
          <div className="pr-modal-row pr-modal-row3">
            <label className="pf-field">
              <span>Quantity *</span>
              <input type="number" min="1" step="any" name="quantity" value={form.quantity}
                     onChange={set} required data-testid="printer-new-rfq-qty"/>
            </label>
            <label className="pf-field">
              <span>Unit *</span>
              <input name="quantity_unit" value={form.quantity_unit} onChange={set} required
                     placeholder="sheets / kg / rolls" data-testid="printer-new-rfq-unit"/>
            </label>
            <label className="pf-field">
              <span>Deadline (days) *</span>
              <input type="number" min="1" max="90" name="deadline_days" value={form.deadline_days}
                     onChange={set} required data-testid="printer-new-rfq-deadline"/>
            </label>
          </div>
          <label className="pf-field">
            <span>{source === "own" ? `Estimated order value (₹, min ₹${OWN_MIN_VALUE.toLocaleString("en-IN")}) *` : "Expected price (₹, optional hint)"}</span>
            <input type="number" min={source === "own" ? OWN_MIN_VALUE : 0} step="any" name="expected_price_hint"
                   required={source === "own"}
                   value={form.expected_price_hint} onChange={set}
                   data-testid="printer-new-rfq-pricehint"
                   placeholder="e.g. 45000"/>
          </label>
          {source === "own" && (
            <p style={{fontSize:11,color:"#64748b",margin:0}} data-testid="printer-new-rfq-own-note">
              You pay the winning quote + GST into escrow. GoPrintPack issues the tax invoice to your GSTIN (input tax credit eligible) and releases payment to the partner after you confirm receipt.
            </p>
          )}
          <button type="submit" disabled={busy || (source === "own" && !me?.is_verified)} className="m-cta m-cta-lg pf-submit"
                  data-testid="printer-new-rfq-submit">
            {busy ? "Posting…" : <>Post sealed RFQ <ArrowRight size={14}/></>}
          </button>
        </form>
      </div>
    </div>
  );
}

const MATERIAL_OPTS = [["paper", "Paper"], ["board", "Board"], ["flexo_film", "Flexo film"], ["ink", "Ink / coating"], ["plate", "Plate / chemical"], ["adhesive", "Adhesive"], ["other", "Other"]];
const FINISHING_OPTS = [["lamination", "Lamination"], ["foiling", "Foiling"], ["embossing", "Embossing / debossing"], ["spot_uv", "Spot UV"], ["die_cutting", "Die-cutting"], ["binding", "Binding"], ["window_patching", "Window patching"], ["other", "Other"]];
